import React, { createContext, useContext, useEffect, useState } from 'react'
import { canvasAuthService } from '../../../common/services/CanvasAuthService'
import { canvasSdkService } from '../services/CanvasSdkService'
import type { SalesforceContextData } from '../types/salesforce'
import { isEmpty } from 'lodash'

const SalesforceContext = createContext<SalesforceContextData | undefined>(
  undefined
)

interface SalesforceContextProviderProps {
  children: React.ReactNode
}

export const SalesforceContextProvider: React.FC<
  SalesforceContextProviderProps
> = ({ children }) => {
  const [contextData, setContextData] = useState<SalesforceContextData>({
    partnerId: '',
    record: undefined,
    organization: { Id: '' },
    actions: {
      openModal: () => {},
      navigateToUrl: () => null,
      navigateToRecord: () => null,
      navigateBack: () => {},
      refreshParent: () => {},
      closeModal: () => {},
    },
    crmId: '',
    customParameters: undefined,
    isAuthInitialized: false,
    authError: null,
    loading: true, // Start with loading: true
    error: undefined,
    isInSalesforceContext: false,
  })

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await canvasAuthService.initialize()
        const partnerId = canvasAuthService.getPartnerId()
        const record = canvasAuthService.getRecord()
        const organization = canvasAuthService.getOrganization()
        const crmId = canvasAuthService.getCrmId()
        const customParameters = canvasAuthService.getCustomParameters()

        setContextData({
          partnerId: partnerId || '',
          record,
          organization: organization || { Id: '' },
          actions: {
            openModal: canvasSdkService.openModal.bind(canvasSdkService),
            navigateToUrl:
              canvasSdkService.navigateToUrl.bind(canvasSdkService),
            navigateToRecord:
              canvasSdkService.navigateToRecord.bind(canvasSdkService),
            navigateBack: canvasSdkService.navigateBack.bind(canvasSdkService),
            refreshParent:
              canvasSdkService.refreshParent.bind(canvasSdkService),
            closeModal: canvasSdkService.closeModal.bind(canvasSdkService),
          },
          crmId: crmId || '',
          customParameters: customParameters || undefined,
          isAuthInitialized: true,
          authError: null,
          loading: false, // Loading complete
          error: undefined,
          isInSalesforceContext: true,
        })
      } catch (error) {
        setContextData(prev => ({
          ...prev,
          isAuthInitialized: false,
          authError:
            'Failed to authenticate. Please reload the page from Salesforce Canvas.',
          loading: false, // Loading complete (with error)
          error: 'salesforce_context_not_available',
          isInSalesforceContext: false,
        }))
      }
    }

    initializeAuth()
  }, [])

  return (
    <SalesforceContext.Provider value={contextData}>
      {children}
    </SalesforceContext.Provider>
  )
}

export const useSalesforceContext = (): SalesforceContextData => {
  const context = useContext(SalesforceContext)

  if (context === undefined || isEmpty(context.partnerId)) {
    return {
      partnerId: '',
      record: undefined,
      organization: { Id: '' },
      actions: {
        openModal: () => {},
        navigateToUrl: () => null,
        navigateToRecord: () => null,
        navigateBack: () => {},
        refreshParent: () => {},
        closeModal: () => {},
      },
      crmId: '',
      customParameters: undefined,
      error: 'salesforce_context_not_available',
      loading: context?.loading ?? false,
      isAuthInitialized: context?.isAuthInitialized ?? false,
      authError: context?.authError ?? null,
      isInSalesforceContext: false,
    }
  }

  return context
}
